import { useState } from "react";

import { useSocket } from "../../lib/socket";
import type { User } from "../../types";
import { AppFrame } from "../../components/AppFrame";
import { MeetingsSidebar } from "../../components/meetings/MeetingsSidebar";
import { NewMeetingDialog } from "../../components/meetings/NewMeetingDialog";
import { Button } from "../../components/ui/Button";

/**
 * The diary's shell: the same frame the other three products render in, with
 * its own navigation and its own toolbar.
 *
 * Its own shell rather than a section of Sales calls, because the two are
 * different jobs done by different people in different places -- one at a desk
 * with a headset, one in a van -- and a field rep opening Pulse should land on
 * their own week rather than on the desk's call pool.
 *
 * It does still share the sales module's API and its score list, which is
 * right: how a visit went is the same question about the same account as how a
 * call went, and two lists of outcomes would be two settings screens that drift
 * apart and one report that cannot add them together.
 *
 * The socket is the shared one rather than a fourth connection. A meeting opens
 * in a pane drawn by the page itself -- see `MeetingsHome` -- so the calendar
 * or feed beside it stays mounted, scrolled where it was.
 */
export function MeetingsShell({ user }: { user: User }) {
  const { connected } = useSocket();
  const [booking, setBooking] = useState(false);

  return (
    <>
      {/* Outside the frame so it is never clipped by a pane, and mounted here
          rather than in a page so booking from any view is the
          same dialog in the same state. */}
      {booking ? (
        <NewMeetingDialog
          onClose={() => setBooking(false)}
          currentUserId={user.id}
        />
      ) : null}

      <AppFrame
        user={user}
        title="Meetings"
        connected={connected}
        sidebar={<MeetingsSidebar connected={connected} userId={user.id} />}
        toolbar={
          <Button size="sm" onClick={() => setBooking(true)}>
            New meeting
          </Button>
        }
      />
    </>
  );
}
